<template>
  <div class="pa-4 mx-auto manage-academy-event-plus" style="max-width: 56rem;">
    <div v-if="loading" class="text-center">
      <circles-loader></circles-loader>
    </div>
    <div v-else-if="!loading && event" v-allowed="'manage_event'">
      <v-card v-if="canCheckin" class="mb-4" elevation="0">
        <v-card-actions class="pa-0">
          <v-btn small block elevation="0" color="primary" outlined @click="openScannerDialog()">
            <v-icon class="mr-1">mdi-qrcode-scan</v-icon>
            {{ $t("message.participant_checkin") }}
          </v-btn>
        </v-card-actions>
      </v-card>

      <header class="manage-academy-event-plus__header mb-6">
        <h1 class="font-roboto mb-1 plusGreyDarken4--text" style="font-size: 1.5rem;">
          <template v-if="eventId == 'new'">
            {{ $t('message.add_event') }}

          </template>
          <template v-else>
            {{ $t('message.edit') }} {{ $t('message.event') }}
          </template>
        </h1>
        <p class="font-roboto plusGreyDarken2--text text-subtitle-1">{{ $t('message.social_event_explained') }}</p>
      </header>

      <v-card v-if="event && event.cancelled" class="mb-4 font-roboto" elevation="0" outlined
        style="background-color: #ff5252;">
        <v-card-text class="text-center white--text py-3">
          {{ $t("message.event_cancelled") }}
        </v-card-text>
      </v-card>
      <v-card v-if="totalSales > 0 && currencySymbol" class="mb-4" elevation="0" outlined>
        <v-card-text class="text-left py-3 font-roboto plusGreyDarken2--text text-subtitle-1">
          {{ $t("message.total_sales") }}: <span class="total-sales text-h6">{{ currencySymbol }}{{ totalSales }}</span>
        </v-card-text>
      </v-card>

      <v-card v-if="event" class="manage-academy-event-plus__section font-roboto mb-4 rounded-lg pt-4 px-6 pb-6"
        elevation="0" outlined>
        <v-card-text class="pa-6">
          <div class="plusGreyDarken4--text mb-4" style="font-size: 1.25rem;">Event details</div>
          <v-row>
            <v-col cols="12" md="7">
              <v-text-field outlined dense v-model="event.title" :label="$t('message.title')"></v-text-field>
              <v-select :items="eventTypes" v-model="event.type" item-text="text" item-value="value"
                :label="$t('message.visibility')" outlined dense></v-select>
              <div v-if="event.type == 'type_regular'" class="supporting-text mb-3 input-message">
                {{ $t("message.public_event_explained") }}
              </div>
              <div v-else class="supporting-text mb-3" style="margin-top:-10px">
                {{ $t("message.private_event_explained") }}
              </div>
              <v-switch v-if="event.type == 'type_private'" dense v-model="event.enableVisibleToMembers"
                :label="$t('message.allow_members_to_see')"></v-switch>
              <v-text-field outlined dense v-model="event.phone" :label="$t('message.phone')">
              </v-text-field>
              <v-text-field outlined dense v-model="event.contact"
                :label="$t('message.contact') + ' (' + $t('message.optional') + ')'">
              </v-text-field>
            </v-col>
            <v-col cols="12" md="5">
              <div class="event-image-upload-zone pa-5 text-center">
                <div v-if="eventImage" class="event-image-upload-zone__preview mb-3">
                  <ImageDisplay :src="eventImage" style="max-width:100%;" />
                  <ImageUpload class="mt-3" :label="$t('message.upload_image')" :add-small="false" block
                    :success-function="successUploadImage"></ImageUpload>
                </div>
                <!-- <v-avatar v-else class="event-image-upload-zone__placeholder mb-3" rounded tile size="120">
                  <img :src="assets('logo-circular-watermarked.png')" alt="" />
                </v-avatar> -->

                <div v-else class="flex flex-col justify-center items-center">
                  <div class="font-roboto text-subtitle-1 plusGreyDarken2--text">Upload a photo of the event</div>
                  <ImageUpload class="mt-3" :label="$t('message.upload_image')" :add-small="false" block
                    :success-function="successUploadImage"></ImageUpload>
                </div>
              </div>
              <div class="text-subtitle-2 plusGreyDarken1--text mt-2 justify-center text-center">Recommended: JPEG, PNG
                up to
                20mb
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <locations :label="$t('message.location')" v-model="location" :display="event.location"></locations>
              <div v-if="missingCityError" :style="'color:' + getColor('red')">{{ $t('message.city_missing') }}</div>
              <div v-if="!location"
                class="font-roboto academy-address-hint text-body-2 plusGreyDarken1--text mb-3 clickable">
                {{ $t("message.academy_address") }}: {{ address }}
                <v-btn x-small icon class="ml-1" @click="copyAddress()">
                  <DocumentDuplicateIcon size="15" class="hero-icon-button"></DocumentDuplicateIcon>
                </v-btn>
              </div>
              <v-text-field class="event-timezone-field" outlined dense :disabled="true" v-model="timeZone"
                :label="$t('message.time_zone')">
              </v-text-field>
              <v-card-actions v-if="event._id" class="px-0">
                <v-text-field outlined dense :readonly="true" v-model="event.publicLink"
                  :label="$t('message.public_link')">
                </v-text-field>
              </v-card-actions>
              <v-card-actions v-if="event._id" class="px-0 pt-0" style="margin-top:-20px">
                <v-btn icon @click="openQRCode()">
                  <QrcodeIcon class="hero-icon-button"></QrcodeIcon>
                </v-btn>
                <v-btn icon @click="copyPublicUrl()">
                  <DocumentDuplicateIcon class="hero-icon-button"></DocumentDuplicateIcon>
                </v-btn>
                <v-btn icon @click="openPublicUrl()">
                  <ExternalLinkIcon class="hero-icon-button"></ExternalLinkIcon>
                </v-btn>
              </v-card-actions>

              <div class="d-flex align-start mb-4">
                <v-switch v-model="event.enableContactForm" hide-details class="mt-1 mr-3" inset
                  color="plusIndigoDarken4"></v-switch>
                <div>
                  <div class="text-subtitle-1 plusGreyDarken1--text">{{ $t('message.activate_contact_form') }}</div>
                  <div class="font-roboto text-subtitle-2 plusGreyDarken1--text font-weight-regular">{{
                    $t('message.activate_contact_form_explained') }}</div>
                </div>
              </div>
              <div class="d-flex align-start mb-2">
                <v-switch v-model="enableDocument" hide-details class="mt-1 mr-3" inset
                  color="plusIndigoDarken4"></v-switch>
                <div class="flex-grow-1">
                  <div class="text-subtitle-1 plusGreyDarken1--text">{{ $t('message.add_document') }}</div>
                  <div class="font-roboto text-subtitle-2 plusGreyDarken1--text font-weight-regular">{{
                    $t('message.add_document_event_explained') }}</div>
                  <div v-if="enableDocument" class="mt-3">
                    <v-autocomplete @input="
                      (event) => {
                        $emit('input', event);
                      }
                    " v-model="event.document" :items="documentTemplates" item-text="text"
                      :label="$t('message.select_document')" item-value="value" clearable outlined
                      dense></v-autocomplete>
                  </div>
                </div>
              </div>

              <v-card-actions v-if="event._id" class="px-0">
                <v-text-field :error="!validIdentifier" :loading="checkingId" outlined dense v-model="eventIdentifier"
                  :label="$t('message.event_id')">
                </v-text-field>
              </v-card-actions>
              <v-textarea class="mt-2" outlined filled rows="3" :label="$t('message.description')"
                v-model="event.description"></v-textarea>
              <div class="font-roboto text-subtitle-1 plusGreyDarken4--text mt-4 mb-2">Page information</div>
              <div class="page-info-editor">
                <VueEditor v-model="event.pageInfo" :editor-toolbar="editorToolbar" />
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      <v-card elevation="0" class="pa-6" outlined v-if="event">
        <div class="plusGreyDarken4--text" style="font-size: 1.25rem;">
          {{ $t('message.date_time') }}
        </div>
        <v-card-text v-if="differentTimezone">
          <div>
            <ExclamationIcon class="hero-icon"></ExclamationIcon>
            <span v-html="$t('message.different_timezone_explained', [browserTimezone])"></span>
          </div>
        </v-card-text>
        <v-card-text class="text-left">
          <div>
            <div class="text-subtitle-1 plusGreyDarken4--text">Event start time</div>
            <DatePicker :show-time="true" v-if="event.date" v-model="event.start" :allow-all-dates="true">
            </DatePicker>
            <div class="error--text" v-if="differentTimezone">
              {{ $t('message.time_at_timezone') }}: <span class="ml-1">{{ timeAtTimezone(event.start) }}</span>
            </div>
          </div>
          <div class="mt-5">
            <div class="text-subtitle-2">Event finish time</div>
            <DatePicker :show-time="true" v-if="event.endDate" v-model="event.end" :allow-all-dates="true">
            </DatePicker>
            <div class="error--text" v-if="differentTimezone">
              {{ $t('message.time_at_timezone') }}: <span class="ml-1">{{ timeAtTimezone(event.end) }}</span>
            </div>
          </div>
        </v-card-text>
      </v-card>
      <v-card v-if="event" class="manage-academy-event-plus__section mb-4 rounded-lg pt-4 px-6 pb-6 mt-6" elevation="0"
        outlined>
        <v-card-title class="plusGreyDarken4--text" style="font-size: 1.25rem;">{{ $t('message.participants')
          }}</v-card-title>
        <v-card-text class="">
          <v-text-field outlined dense :label="$t('message.max_attendance')"
            v-model="event.maxAttendance"></v-text-field>

          <div class="d-flex align-center justify-start mb-2">
            <v-switch v-model="event.hideParticipants" hide-details dense class="plusGreyDarken1--text" inset
              color="plusIndigoDarken4" label="Hide participants"></v-switch>
          </div>
        </v-card-text>
        <v-card-text class="">
          <div v-if="loadingParticipants">
            <v-skeleton-loader type="list-item-avatar, list-item-avatar"></v-skeleton-loader>
          </div>
          <v-list v-else>
            <v-subheader>
              <span class="mr-2 mb-1">
                <v-icon size="20" color="gray">mdi-account-group-outline</v-icon>
              </span>
              {{ $t("message.participants") }}: {{ totalParticipants }}
              <v-spacer></v-spacer>
              <v-btn v-if="!isSmallerDisplay()" :loading="downloadingParticipants" small outlined color="primary"
                elevation="0" @click="downloadParticipantList()">
                <v-icon class="mr-1">mdi-cloud-download-outline</v-icon>
                {{ $t("message.download") }}
              </v-btn>
            </v-subheader>
            <v-list-item dense class="py-0">
              <v-list-item-content class="py-1">
                <div class="supporting-text">
                  <v-icon size="14" class="mr-1">mdi-account-check-outline</v-icon>
                  {{ $t("message.members") }}: {{ participants.length }}
                  <span class="ml-3">
                    <v-icon size="14" class="mr-1">mdi-account-outline</v-icon>
                    {{ $t("message.non_members") }}: {{ externalParticipants.length }}
                  </span>
                </div>
              </v-list-item-content>
            </v-list-item>
            <v-divider></v-divider>
            <div v-for="(user, i) in allParticipants" :key="'p_' + i + '_' + user._id">
              <v-list-item link @click.stop="displayTransactionDetails(user)">
                <v-list-item-avatar>
                  <ImageDisplay v-if="user.image" :src="user.image" />
                  <img v-else :src="assets('default-profile-avatar.png')" />
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>
                    <v-icon v-if="user.isMember" size="16" color="accent" class="mr-1 mb-1">mdi-account-check</v-icon>
                    <span>{{ user.name && user.name !== user.email ? user.name : $t('message.visitor') }}</span>
                    <v-chip color="accent" x-small class="ml-1" outlined
                      v-if="user.transaction && user.transaction.quantity > 1">+{{ user.transaction.quantity - 1
                      }}</v-chip>
                  </v-list-item-title>
                  <v-list-item-subtitle>{{ user.email }}</v-list-item-subtitle>
                  <v-list-item-subtitle v-if="user.transaction && user.transaction.time" class="text-caption">
                    <template v-if="isTransactionFree(user.transaction)">{{ $t('message.free_entry') }}</template>
                    <template v-else>{{ $t('message.paid_on') }} {{ new
                      Date(user.transaction.time).toLocaleDateString($i18n.locale) }}<span
                        v-if="user.transaction.amount" class="ml-1">· {{ currencySymbol }}{{ user.transaction.amount
                        }}</span></template>
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action v-if="event.document">
                  <v-icon color="success" v-if="user.documentCompleted">mdi-text-box-check-outline</v-icon>
                  <v-icon v-else>mdi-text-box-remove-outline</v-icon>
                </v-list-item-action>
                <v-list-item-action>
                  <v-icon color="success"
                    v-if="user.transaction && isTransactionFree(user.transaction)">mdi-currency-usd-off</v-icon>
                  <v-icon color="error"
                    v-else-if="user.transaction && user.transaction.refunds && user.transaction.refunds.length > 0">mdi-cash-refund</v-icon>
                  <v-icon color="success"
                    v-else-if="user.transaction && user.transaction.paidWithCash">mdi-cash-100</v-icon>
                  <v-icon color="success" v-else-if="user.transaction">mdi-credit-card-outline</v-icon>
                </v-list-item-action>
                <v-list-item-action>
                  <v-icon v-if="user.checkedIn" color="success">mdi-checkbox-marked-circle-outline</v-icon>
                  <v-icon color="gray" v-else>mdi-location-enter</v-icon>
                </v-list-item-action>
              </v-list-item>
              <v-divider v-if="i + 1 < allParticipants.length"></v-divider>
            </div>
          </v-list>
        </v-card-text>
      </v-card>
      <v-card v-if="event && event._id" class="manage-academy-event-plus__section mb-4 rounded-lg" elevation="0"
        outlined>
        <div class="text-subtitle-1 pa-6 pb-0">
          {{ $t('message.alt_image') }}
        </div>
        <v-card-text class="supporting-text px-6 pt-2">
          {{ $t('message.alt_image_explained') }}
        </v-card-text>
        <v-card-text class="px-6 pt-0">
          <div xs12 v-if="altEventImage" style="overflow-y: hidden; max-height:350px">
            <ImageDisplay :src="altEventImage" style="width:100%" />
          </div>
          <CoverImage :success-function="uploadedAltImage"></CoverImage>
        </v-card-text>
        <v-card-actions class="px-6">
          <v-btn v-if="altEventImage" @click="clearAltImage()" color="error" elevation="0" block outlined small>
            <TrashIcon class="hero-icon-button"></TrashIcon>
          </v-btn>
        </v-card-actions>
        <v-card-actions class="px-6 pt-0">
          <v-switch v-model="event.displayAcademyImage" :label="$t('message.display_academy_image')"></v-switch>
        </v-card-actions>
      </v-card>
      <v-card elevation="0" class="mt-2" outlined v-if="event && eventId != 'new'">
        <div class="pa-2 mb-2">
          <div class="text-subtitle-1">
            <v-switch :label="$t('message.ticket')" class="ml-1" v-model="addTicket" hide-details></v-switch>
          </div>
          <div class="text-caption">
            {{ $t('message.tickets_explained') }}
          </div>
        </div>
        <v-card-text v-if="addTicket && event.ticket" class="pt-0 mt-0">
          <v-text-field hide-details outlined dense :rules="[numberRule]" :label="$t('message.ticket_price')"
            v-model="amountDecimal"></v-text-field>
          <div class="mb-2">
            <ExclamationIcon size="20" color="gray"></ExclamationIcon>{{ $t('message.tickets_explained_nostripe') }}
          </div>
          <v-text-field outlined dense
            :label="$t('message.button_label') + ' (' + $t('message.optional').toLowerCase() + ')'"
            v-model="event.ticket.buttonLabel"></v-text-field>
          <v-text-field outlined dense :label="$t('message.ticket_description')" v-model="event.ticket.title"
            maxlength="100" counter="100"></v-text-field>
          <v-switch dense :label="$t('message.hide_ticket')" v-model="event.ticket.hide"></v-switch>
          <span class="supporting-text input-message">{{ $t('message.hide_ticket_explained') }}</span>
        </v-card-text>
        <v-card-text v-if="addTicket">
          <div class="text-caption mb-2">{{ $t('message.extra_tickets') }}</div>
          <v-card elevation="0" outlined>
            <div v-for="(t, i) in tickets" class="ma-2" :key="'extra_ticket_' + i">
              <div class="mt-2 pa-3">
                <v-text-field outlined dense :rules="[numberRule]" :label="$t('message.ticket_price')"
                  v-model="t.amountDecimal">
                </v-text-field>
                <v-text-field outlined dense
                  :label="$t('message.button_label') + ' (' + $t('message.optional').toLowerCase() + ')'"
                  v-model="t.buttonLabel"></v-text-field>
                <v-text-field outlined dense :label="$t('message.ticket_description')" v-model="t.title" maxlength="100"
                  counter="100"></v-text-field>
                <v-card-actions class="mt-0 pt-0">
                  <v-switch dense :label="$t('message.hide_ticket')" v-model="t.hide"></v-switch>
                  <v-spacer></v-spacer>
                  <v-btn small icon @click="removeExtraTicket(i)">
                    <TrashIcon class="hero-icon-button"></TrashIcon>
                  </v-btn>
                </v-card-actions>
              </div>
            </div>
          </v-card>
          <div class="mt-2">
            <v-btn small elevation="0" class="mt-3" @click="addExtraTicket()">{{ $t('message.add_extra_ticket')
              }}</v-btn>
          </div>
        </v-card-text>
        <div v-if="addTicket">
          <v-divider></v-divider>
          <v-card-text>
            <v-switch hide-details dense :label="$t('message.skip_entry')" v-model="event.skipTicket"></v-switch>
            <span class="text-caption mb-2">{{ $t('message.skip_entry_explained') }}</span>
          </v-card-text>
        </div>
      </v-card>
      <footer v-if="event" class="manage-academy-event-plus__footer mt-6 pb-4 mb-15">
        <div class="d-flex flex-wrap align-center" style="gap: 12px;">
          <PlusButton :block="isSmallerDisplay()" small @click.stop="back2Events()">
            <span class="font-roboto text-button d-inline">{{ $t("message.back") }}</span>
          </PlusButton>

          <PlusButton :block="isSmallerDisplay()" v-if="eventId == 'new'" small intent="primary"
            @click.stop="addEvent()">
            <span class="font-roboto text-button d-inline">{{ $t("message.add_event") }}</span>
          </PlusButton>

          <PlusButton :block="isSmallerDisplay()" v-else-if="event.active" small intent="primary"
            @click.stop="editEvent()">
            <span class="font-roboto text-button d-inline">{{ $t("message.update") }}</span>
          </PlusButton>

          <PlusButton key="sell-ticket-btn" :block="isSmallerDisplay()"
            v-if="eventId != 'new' && addTicket && event.active" small intent="primary"
            :disabled="event.maxAttendance && event.maxAttendance > 0 && event.maxAttendance == totalParticipants ? true : false"
            @click.stop="openSellTicketDialog()">
            <span class="font-roboto text-button d-inline">{{ $t("message.sell_ticket") }}</span>
          </PlusButton>

          <PlusButton :block="isSmallerDisplay()" v-if="eventId != 'new'" color="primary" outlined elevation="0" small
            @click="sendEmailsDialog = true">
            <span class="font-roboto text-button d-inline">{{ $t("message.email_participants") }}</span>
          </PlusButton>
          <v-spacer></v-spacer>
          <PlusButton :block="isSmallerDisplay()" v-if="eventId != 'new' && event.active" small intent="error"
            @click.stop="cancelEventDialog = true">
            <span class="font-roboto text-button d-inline">Cancel Event</span>
          </PlusButton>
        </div>
        <!-- <div v-else>
          <v-btn outlined block elevation="0" color="primary" @click.stop="back2Events()">{{ $t("message.cancel")
          }}</v-btn>

          <v-btn v-if="eventId == 'new'" class="mt-2" color="primary" block elevation="0" depressed
            @click.stop="addEvent()">{{ $t("message.add_event") }}</v-btn>

          <v-btn v-else-if="event.active" class="mt-2" color="primary" block elevation="0" depressed
            @click.stop="editEvent()">{{ $t("message.update") }}</v-btn>
          <v-btn small class="mt-2" elevation="0" outlined block v-if="eventId != 'new' && addTicket && event.active"
            color="primary"
            :disabled="event.maxAttendance && event.maxAttendance > 0 && event.maxAttendance == totalParticipants ? true : false"
            @click.stop="openSellTicketDialog()">
            {{ $t("message.sell_ticket") }}
          </v-btn>
          <v-btn small class="mt-2" block elevation="0" outlined v-if="eventId != 'new'" color="primary"
            @click="sendEmailsDialog = true">
            {{ $t("message.email_participants") }}
          </v-btn>
          <v-btn class="mt-2" block elevation="0" small v-if="eventId != 'new' && event.active" color="error"
            @click.stop="cancelEventDialog = true">
            <TrashIcon class="hero-icon-button"></TrashIcon>
            <span>{{ $t("message.cancel") }}</span>
          </v-btn>
          <v-btn small class="mt-2" block elevation="0" outlined @click.stop="goBack()">{{ $t("message.back")
          }}</v-btn>
        </div> -->
      </footer>
    </div>
    <v-dialog v-model="sellTicketDialog" width="90%">
      <v-card v-if="event && event.ticket" outlined>
        <v-card-title>
          {{ $t("message.sell_ticket") }}
        </v-card-title>
        <v-card-text>
          <v-autocomplete hide-details v-model="selectedMember" :items="members" :label="$t('message.pick_member')"
            item-text="text" item-value="value" clearable outlined dense></v-autocomplete>
          <div class="text-center">{{ $t("message.or") }}</div>
          <v-text-field hide-details outlined dense label="Email" v-model="email"></v-text-field>
          <v-switch hide-details v-if="event.ticket.amountDecimal > 0" v-model="freeTicket"
            :label="$t('message.free')"></v-switch>
        </v-card-text>
        <div v-if="email || selectedMember">
          <v-card-actions v-if="event && !isSmallerDisplay()">
            <v-spacer></v-spacer>
            <v-btn outlined v-if="event.ticket && amountDecimal > 0 && paymentsReady && !freeTicket"
              :disabled="sellingTicket" color="primary" small elevation="0" @click="sellTicket(false)">
              <v-icon>mdi-credit-card-outline</v-icon>
              {{ event.ticket.buttonLabel }} {{ currencySymbol }}{{ event.ticket.amountDecimal }}
              <v-progress-circular v-if="sellingTicket" indeterminate color="white"></v-progress-circular>
            </v-btn>
            <v-btn :disabled="sellingTicket" v-if="event.ticket && (email || selectedMember)" color="primary" outlined
              small elevation="0" @click="sellTicket(true)">
              <v-icon>mdi-cash-100</v-icon>{{ event.ticket.buttonLabel }} {{ currencySymbol }}{{
                event.ticket.amountDecimal }}
            </v-btn>
            <v-spacer></v-spacer>
          </v-card-actions>
          <div v-else-if="event && isSmallerDisplay()">
            <v-card outlined elevation="0" class="mt-1">
              <v-card-text class="text-center">
                <div class="subtitle-1">{{ event.ticket.buttonLabel }}</div>
                <div>{{ currencySymbol }}{{ event.ticket.amountDecimal }}</div>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn outlined v-if="event.ticket && amountDecimal > 0 && paymentsReady && !freeTicket"
                  :disabled="sellingTicket" color="primary" small elevation="0" @click="sellTicket(false)">
                  <v-icon>mdi-credit-card-outline</v-icon>
                  {{ $t("message.credit") }}
                  <v-progress-circular v-if="sellingTicket" indeterminate color="white"></v-progress-circular>
                </v-btn>
                <v-btn :disabled="sellingTicket" v-if="event.ticket && (email || selectedMember)" color="primary"
                  outlined small elevation="0" @click="sellTicket(true)">
                  <v-icon>mdi-cash-100</v-icon>{{ $t("message.cash") }}
                </v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </div>
          <div v-if="tickets && tickets.length > 0">
            <div v-if="isSmallerDisplay()">
              <template v-for="(t, i) in tickets">
                <v-card :key="'extra_ticket_option_' + i + '_' + event._id" outlined elevation="0" class="mt-1">
                  <v-card-text class="text-center">
                    <div class="subtitle-1">{{ t.buttonLabel }}</div>
                    <div>{{ currencySymbol }}{{ t.amountDecimal }}</div>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn outlined v-if="t.amountDecimal > 0 && paymentsReady && !freeTicket" :disabled="sellingTicket"
                      color="primary" small elevation="0" @click="sellTicket(false, i)">
                      <v-icon>mdi-credit-card-outline</v-icon>
                      {{ $t("message.credit") }}
                      <v-progress-circular v-if="sellingTicket" indeterminate color="white"></v-progress-circular>
                    </v-btn>
                    <v-btn :disabled="sellingTicket" color="primary" outlined small elevation="0"
                      @click="sellTicket(true, i)">
                      <v-icon>mdi-cash-100</v-icon>{{ $t("message.cash") }}
                    </v-btn>
                    <v-spacer></v-spacer>
                  </v-card-actions>
                </v-card>
              </template>
            </div>
            <div v-else>
              <v-card-actions v-for="(t, i) in tickets" :key="'extra_ticket_option_' + i + '_' + event._id">
                <v-spacer></v-spacer>
                <v-btn outlined v-if="t.amountDecimal > 0 && paymentsReady && !freeTicket" :disabled="sellingTicket"
                  color="primary" small elevation="0" @click="sellTicket(false, i)">
                  <v-icon>mdi-credit-card-outline</v-icon>
                  {{ t.buttonLabel }} {{ currencySymbol }}{{ t.amountDecimal }}
                </v-btn>
                <v-btn :disabled="sellingTicket" color="primary" outlined small elevation="0"
                  @click="sellTicket(true, i)">
                  <v-icon>mdi-cash-100</v-icon>{{ t.buttonLabel }} {{ currencySymbol }}{{
                    t.amountDecimal
                  }}
                </v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </div>
          </div>
        </div>
        <v-card-text class="mt-1">
          <v-progress-circular v-if="sellingTicket" indeterminate color="white"></v-progress-circular>
          <v-btn :block="isSmallerDisplay() ? true : false" small elevation="0" @click="sellTicketDialog = false">
            {{ $t("message.cancel") }}
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="participantDetailsDialog">
      <v-card v-if="selectedParticipant">
        <v-card-text v-if="selectedParticipant.documentCompleted">
          <v-icon>mdi-text-box-check-outline</v-icon>
          <b>{{ $t("message.document") }}:</b>
          <span class="clickable" @click="goToCompletedDocument(selectedParticipant.documentCompleted._id)"
            style="margin-left:5px">{{
              selectedParticipant.documentCompleted.name }}</span>
        </v-card-text>
        <v-card-text v-if="selectedParticipant.transaction">
          <v-icon
            v-if="selectedParticipant.transaction && selectedParticipant.transaction.paidWithCash">mdi-cash-100</v-icon>
          <v-icon v-else>mdi-credit-card-outline</v-icon>
          <b>{{ $t("message.transaction_id") }}:</b>
          <span class="clickable" @click="goToTransaction(selectedParticipant.transaction._id)"
            style="margin-left:5px">{{
              selectedParticipant.transaction._id }}</span>
        </v-card-text>
        <v-card-text v-if="selectedParticipant.transaction">
          <b>{{ $t("message.date") }}:</b>
          <span style="margin-left:5px">{{ $d(new
            Date(selectedParticipant.transaction.time), "long", $i18n.locale) }}</span>
        </v-card-text>
        <v-card-text v-if="selectedParticipant.transaction">
          <b>TOTAL:</b>
          <span style="margin-left:5px">{{ selectedParticipant.transaction.amount }} {{ currency.toUpperCase() }}</span>
        </v-card-text>
        <v-card-text style="color:red"
          v-if="selectedParticipant.transaction && selectedParticipant.transaction.refunds && selectedParticipant.transaction.refunds.length > 0">
          <span>{{ $t("message.refunded") }}</span>
        </v-card-text>
        <v-card-text v-show="confirmRefundTransactionDiv">
          <v-switch :label="$t('message.confirm_refund')" v-model="confirmRefundTransaction"></v-switch>
        </v-card-text>
        <v-card-actions v-if="!isSmallerDisplay()">
          <v-btn outlined small elevation="0" color="primary"
            @click="goToTransaction(selectedParticipant.transaction._id)" v-if="selectedParticipant.transaction">{{
              $t("message.details") }}</v-btn>
          <v-btn v-if="selectedParticipant.documentCompleted" outlined small elevation="0" color="primary"
            @click="goToCompletedDocument(selectedParticipant.documentCompleted._id)">{{
              $t("message.document") }}</v-btn>
          <v-btn small elevation="0" outlined color="primary" @click="confirmRefund()"
            v-if="selectedParticipant.transaction && !selectedParticipant.transaction.paidWithCash && (!selectedParticipant.transaction.refunds || selectedParticipant.transaction.refunds.length == 0) && !confirmRefundTransaction && !confirmRefundTransactionDiv">{{
              $t("message.refund") }}</v-btn>
          <v-btn small elevation="0" outlined color="primary"
            @click="refundTransaction(selectedParticipant.transaction._id)"
            v-if="selectedParticipant.transaction && selectedParticipant.transaction.paymentIntentId && !selectedParticipant.transaction.paidWithCash && confirmRefundTransaction">{{
              $t("message.confirm_refund") }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn small elevation="0" color="error" v-if="!selectedParticipant.removed"
            @click="removeParticipantFromEvent(selectedParticipant)">
            <TrashIcon class="hero-icon-button"></TrashIcon>{{ $t("message.remove_from_event") }}
          </v-btn>
          <v-btn small elevation="0" color="primary" v-else @click="addParticipantToEvent(selectedParticipant)">{{
            $t("message.add_again") }}</v-btn>
          <v-btn small elevation="0" @click="closeTransactionDetails()">{{ $t("message.close")
            }}</v-btn>
        </v-card-actions>
        <div v-else>
          <v-btn outlined block small elevation="0" color="primary"
            @click="goToTransaction(selectedParticipant.transaction._id)" v-if="selectedParticipant.transaction">{{
              $t("message.details") }}</v-btn>
          <v-btn class="mt-1" block small elevation="0" outlined color="primary" @click="confirmRefund()"
            v-if="selectedParticipant.transaction && !selectedParticipant.transaction.paidWithCash && (!selectedParticipant.transaction.refunds || selectedParticipant.transaction.refunds.length == 0) && !confirmRefundTransaction && !confirmRefundTransactionDiv">{{
              $t("message.refund") }}</v-btn>
          <v-btn class="mt-1" block small elevation="0" outlined color="primary"
            @click="refundTransaction(selectedParticipant.transaction._id)"
            v-if="selectedParticipant.transaction && !selectedParticipant.transaction.paidWithCash && confirmRefundTransaction">{{
              $t("message.confirm_refund") }}</v-btn>
          <v-btn class="mt-1" block small elevation="0" color="error" v-if="!selectedParticipant.removed"
            @click="removeParticipantFromEvent(selectedParticipant)">
            <TrashIcon class="hero-icon-button"></TrashIcon>{{ $t("message.remove_from_event") }}
          </v-btn>
          <v-btn class="mt-1" block small elevation="0" color="primary" v-else
            @click="addParticipantToEvent(selectedParticipant)">{{ $t("message.add_again") }}</v-btn>
          <v-btn class="mt-1" block small elevation="0" @click="closeTransactionDetails()">{{
            $t("message.close") }}</v-btn>
          <v-btn class="mt-1" v-if="!selectedParticipant.checkedIn" small block elevation="0" outlined
            @click="sendEventQRCode()">
            <v-icon class="mr-1">mdi-qrcode</v-icon>{{ $t("message.send_qr_code") }}
          </v-btn>
          <v-btn color="primary" class="mt-1" v-if="!selectedParticipant.checkedIn" small block elevation="0" outlined
            @click="submitParticipantAttendance()">
            <v-progress-circular size="20" v-if="addingParticipant" indeterminate
              color="secondary"></v-progress-circular>
            <v-icon v-else class="mr-1">mdi-location-enter</v-icon>
            {{ $t("message.participant_checkin") }}
          </v-btn>
        </div>
        <div v-if="!isSmallerDisplay() && !selectedParticipant.checkedIn">
          <v-btn small block elevation="0" outlined @click="sendEventQRCode()">
            <v-icon class="mr-1">mdi-qrcode</v-icon>{{ $t("message.send_qr_code") }}
          </v-btn>
          <v-btn class="mt-1" small block elevation="0" outlined @click="submitParticipantAttendance()">
            <v-progress-circular size="20" v-if="addingParticipant" indeterminate color="primary"></v-progress-circular>
            <v-icon v-else class="mr-1">mdi-location-enter</v-icon>
            {{ $t("message.participant_checkin") }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
    <v-dialog v-model="cancelEventDialog" max-width="90%">
      <v-card class="pa-2" outlined>
        <v-card-title>{{ $t('message.are_you_sure') }}</v-card-title>
        <v-card-actions>
          <v-btn elevation="0" color="error" @click.stop="cancelEvent()">{{ $t('message.cancel') }}</v-btn>
          <v-btn elevation="0" @click.stop="cancelEventDialog = false">{{ $t('message.close')
            }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="sendEmailsDialog" max-width="90%">
      <v-card style="height: 600px" class="text-xs-left" outlined>
        <v-card-title>
          <span style="margin-right: 5px">{{ $t("message.send_email_to") }} {{ $t("message.participants") }}</span>
        </v-card-title>
        <v-card-title>
          <v-text-field :label="$t('message.subject')" v-model="emailSubject"></v-text-field>
        </v-card-title>
        <div class="supporting-text" style="margin-left:5px">{{ $t("message.only_one_image") }}</div>
        <div style="height: 290px; overflow-y: auto" class="mb-2">
          <VueEditor :style="'background-color:white;color:black'" v-model="emailContents"
            :editor-toolbar="editorToolbar" />
        </div>
        <v-card-text v-if="processingImages" class="p1-0 pr-0 text-center">
          <v-progress-linear :value="processingImagesProgress" :query="true" color="success" height="20"
            buffer-value="0" stream>
            <template v-slot:default="{ }">
              <span class="white--text">{{ $t("message.processing_images") }}</span>
            </template>
          </v-progress-linear>
        </v-card-text>
        <v-card-actions>
          <v-btn :loading="sendingEmails" outlined color="primary" :disabled="sendingEmails"
            @click="sendEmailToParticipants()">
            {{ $t("message.send") }}
          </v-btn>
          <v-btn color="error" @click="sendEmailsDialog = false">{{
            $t("message.cancel")
            }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="checkinDialog" width="90%">
      <v-card outlined>
        <v-card-text class="text-center text-h4">
          {{ $t('message.participant_checkin') }}
        </v-card-text>
        <v-card-text v-if="loadingScanner">
          <v-progress-linear rounded height="50" indeterminate color="secondary"></v-progress-linear>
        </v-card-text>
        <div class="text-center pa-2">
          <CameraCodeScanner v-if="displayScanner" style="max-width: 550px; margin:auto" @scan="processCheckinFromQr"
            @load="onLoad"></CameraCodeScanner>
        </div>
        <v-card-text class="text-center" v-if="scannerMessage">
          <div v-if="scannerMessage.error">
            <v-icon size="50" color="error">mdi-close-circle-outline</v-icon>
          </div>
          <div v-else>
            <v-icon size="150" color="success">mdi-checkbox-marked-circle-outline</v-icon>
          </div>
          <div v-if="scannerMessage.success">
            <div class="text-h6">{{ scannerMessage.message }}</div>
            <div class="text-h4">{{ $t('message.welcome') }} {{ scannerMessage.user }}</div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn small elevation="0" block @click.stop="closeScanner()">{{ $t('message.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="qrCodeDialog" max-width="280">
      <v-card outlined v-if="qrCodeUrl">
        <v-card-title>{{ event && event.title }}</v-card-title>
        <v-card-text class="text-center">
          <div style="display: inline-block; padding: 5px; border: 2px solid darkslategrey; border-radius: 5px;">
            <vue-qr :text="qrCodeUrl" :size="220"></vue-qr>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn elevation="0" @click="qrCodeDialog = false;">{{ $t('message.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import CirclesLoader from "@/components/displays/CirclesLoader.vue";
import CoverImage from "@/components/inputs/CoverImageUpload.vue";
import DatePicker from "@/components/inputs/DateTimePicker.vue";
import ImageUpload from "@/components/inputs/ImageUploadCroppie.vue";
import Locations from "@/components/inputs/LocationsAutocomplete.vue";
import Api from "@/services/Api";
import { CameraCodeScanner } from "vue-barcode-qrcode-scanner";
import VueQr from 'vue-qrcode-component';
import { VueEditor } from "vue2-editor";

import { Environment } from "@/services/Environment";
import {
  DocumentDuplicateIcon,
  ExclamationIcon,
  ExternalLinkIcon,
  QrcodeIcon,
  TrashIcon,
} from '@vue-hero-icons/outline';
import PlusButton from "../../components/plus/PlusButton.vue";

export default {
  name: "ManageAcademyEventPlus",
  components: {
    CirclesLoader,
    ImageUpload,
    Locations,
    ExternalLinkIcon,
    DocumentDuplicateIcon,
    QrcodeIcon,
    TrashIcon,
    ExclamationIcon,
    DatePicker,
    VueEditor,
    CoverImage,
    CameraCodeScanner,
    PlusButton,
    VueQr
  },
  data() {
    return {
      loading: true,
      uploading: false,
      emailSubject: null,
      qrCodeDialog: false,
      qrCodeUrl: null,
      checkinDialog: false,
      scannerMessage: null,
      qrScannerControls: null,
      checkingAttendance: false,
      processingImagesProgress: 20,
      processingImages: false,
      displayScanner: false,
      loadingScanner: false,
      emailContents: null,
      editorToolbar: this.getEditorToolbar(),
      sendEmailsDialog: false,
      sessionId: this.$route.query.s,
      stripeSessionStatus: this.$route.query.status,
      eventId: this.$route.params.eventId,
      confirmRefundTransactionDiv: false,
      enableDocument: false,
      confirmRefundTransaction: false,
      event: null,
      checkingQrCode: false,
      participantDetailsDialog: false,
      selectedParticipant: null,
      eventImage: null,
      canCheckin: false,
      cancelEventDialog: false,
      altEventImage: null,
      externalParticipants: [],
      participants: [],
      totalSales: 0,
      removedParticipants: [],
      documentTemplates: [],
      freeTicket: false,
      participantsActivator: false,
      sellTicketDialog: false,
      missingCityError: false,
      addTicket: false,
      downloadingParticipants: false,
      loadingParticipants: false,
      differentTimezone: false,
      browserTimezone: null,
      paymentsReady: false,
      paymentMethod: null,
      location: null,
      timeZone: null,
      address: null,
      email: null,
      sendingEmails: false,
      selectedMember: null,
      amountDecimal: 0,
      currency: null,
      eventIdentifier: null,
      addingParticipant: false,
      checkingId: false,
      validIdentifier: true,
      currencySymbol: null,
      selectedDate: null,
      selectedTime: null,
      sellingTicket: false,
      members: [],
      tickets: [],
      minDate: null,
      allowedMinutes: [0, 15, 30, 45],
      numberRule: v => {
        if (v && !v + "".trim()) return true;
        if (v && !isNaN(parseFloat(v)) && v >= 0) return true;
        return this.$t('message.invalid_number');
      },
      eventTypes: [
        { text: this.$t('message.public'), value: "type_regular" },
        { text: this.$t('message.private'), value: "type_private" },
        { text: this.$t('message.seminar'), value: "type_seminar" }
      ],
    };
  },
  created() {
    this.loading = true;
    if (this.stripeSessionStatus == "success") {
      //Push completed
      if (this.sessionId) {
        this.completePurchase(this.sessionId);
      }
    } else if (this.sessionId && this.stripeSessionStatus == "cancel") {
      this.cancelPurchase(this.sessionId);
    }
    this.init();
  },
  beforeRouteUpdate(to, from, next) {
    this.eventId = to.params.eventId;
    this.init();
    next();
  },
  watch: {
    enableDocument: {
      handler: function (newValue) {
        if (!newValue)
          this.event.document = null;
      }
    },
    location: {
      handler: function (newValue) {
        if (newValue && !newValue.city) {
          this.missingCityError = true;
        } else {
          this.event.geolocated = newValue;
          if (newValue && !newValue.timeZone)
            this.updateTimeZone(newValue);
          this.missingCityError = false;
        }
      }
    },
    eventIdentifier: {
      handler: function (newValue) {
        if (newValue) {
          let term = newValue.trim().toLowerCase();
          term = this.removeSpecialCharacters(term);
          if (term != this.event.identifier) {
            this.checkIdentifier(term);
            this.eventIdentifier = term;
          }
        }
      }
    },
    selectedMember: {
      immediate: true,
      handler: function (newValue) {
        if (newValue) this.getPaymentMethod();
      },
    },
  },
  methods: {
    successUploadImage(uploadedFileData) {
      if (uploadedFileData) {
        this.event.image = uploadedFileData.location;
        this.eventImage = uploadedFileData.original;
      }
    },
    uploadedAltImage(uploadedFile) {
      if (uploadedFile) {
        this.event.altImage = uploadedFile;
        this.altEventImage = uploadedFile;
      }
    },
    getDocumentTemplates() {
      Api()
        .get(
          "documents/academy/" + this.$store.state.managedAcademy + "/templates?active=true&type=public"
        )
        .then((response) => {
          const data = response.data;
          if (data) {
            const templates = data;
            if (templates) {
              for (const d of templates) {
                const text = d.name;
                const value = d._id;
                if (d.active)
                  this.documentTemplates.push({ text: text, value: value });
              }
            }
          }
        });
    },
    back2Events() {
      this.navigateTo({
        name: "calendar-adminplus-tab",
        params: { tabId: 1 },
      });
    },
    init() {
      const d = new Date();
      Api()
        .get("admins/academy/" + this.$store.state.selectedAcademy)
        .then((response) => {
          if (response.data) {
            this.address = response.data.address;
            if (response.data.stripeAccountId && response.data.stripeAccountId != '')
              this.paymentsReady = true;
            this.currency = response.data.currency;
            this.currencySymbol = response.data.currencySymbol ? response.data.currencySymbol : '$';
            if (this.eventId == 'new') {
              this.selectedDate = d.toISOString().substr(0, 10);
              this.selectedTime = d.getHours() + ':00';
              this.loading = false;
              this.event = {
                date: new Date(),
                title: null,
                timeZone: null,
                type: 'type_regular',
                document: null,
                ticket: {
                  amountDecimal: 0,
                  title: null,
                  hide: false,
                  buttonLabel: this.$t('message.get_ticket')
                }
              };
              const endDate = new Date();
              endDate.setHours(endDate.getHours() + 2);
              this.event.endDate = endDate;
            } else {
              this.getEvent();
              this.getDocumentTemplates();
            }
          }
        });
      Api()
        .get("admins/academy/" + this.$store.state.selectedAcademy + "/members")
        .then((response) => {
          const data = response.data;
          if (data) {
            for (const m of data.approved) {
              this.members.push({ value: m._id, text: m.fName + " " + m.lName });
            }
          }
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        });
    },
    downloadParticipantList() {
      this.downloadingParticipants = true;
      Api()
        .get(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/events/" + this.eventId + "/participants?csv=yes"
        )
        .then((response) => {
          this.downloadingParticipants = false;
          let blob = new Blob([response.data], { type: "text/csv" });
          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download =
            this.event.title +
            "_Participants_" +
            new Date().toISOString() +
            "_" +
            ".csv";
          link.click();
          this.showAlert("success", this.$t("message.success"));
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        });
    },
    sendEventQRCode() {

      Api()
        .post(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/events/" + this.eventId + "/send/qr",
          { participantId: this.selectedParticipant._id, email: this.selectedParticipant.email }
        )
        .then(() => {
          this.showAlert("success", this.$t("message.sent"));
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        });
    },
    openScannerDialog() {
      this.checkinDialog = true;
      this.loadingScanner = true;
      this.displayScanner = true;
    },
    closeScanner() {
      if (this.qrScannerControls) {
        this.qrScannerControls.stop();
      }
      this.checkinDialog = false;
      this.displayScanner = false;
      this.checkingQrCode = false;
      this.scannerMessage = null;
    },
    submitParticipantAttendance(qrId) {
      const h = this.event.start.getHours();
      const m = this.event.start.getMinutes();
      let startTime = parseFloat(h + "." + String(m).padStart(2, "0"));
      this.addingParticipant = true;
      let data = {
        event: this.eventId,
        qr: qrId,
        source: 'qr',
        startTime: startTime,
        date: this.event.start
      };
      let fromScanner = true;

      if (this.selectedParticipant) {
        fromScanner = false;
        data['source'] = 'manual';
        data['user'] = this.selectedParticipant._id;
      }
      Api()
        .patch(
          "calendar/admin/academy/" + this.$store.state.managedAcademy + "/event/" + this.eventId + "/attendance",
          data
        )
        .then((response) => {
          if (response && response.data && response.data.success) {
            this.addingParticipant = false;
            if (fromScanner) {
              this.scannerMessage = {
                error: false,
                success: true,
                user: response.data.user,
                message: this.$t("message.success")
              };
              this.displayScanner = false;
              this.closeTransactionDetails();
            } else {
              this.closeTransactionDetails();
              this.showAlert("success", "Oss!");
            }
            //Process checked in user
            let found = false;
            for (let i of this.externalParticipants) {
              if (i._id == data.user) {
                i.checkedIn = true;
                found = true;
                break;
              }
            }
            if (!found) {
              for (let i of this.participants) {
                if (i._id == data.user) {
                  i.checkedIn = true;
                  found = true;
                  break;
                }
              }
            }
          } else if (response.data.duplicate) {
            this.checkingQrCode = false;
            if (fromScanner)
              this.scannerMessage = {
                error: true,
                message: this.$t("message.already_used")
              };
          } else {
            this.checkingQrCode = false;
            if (fromScanner)
              this.scannerMessage = {
                error: true,
                message: this.$t("message.invalid_event")
              };
          }
        })
        .catch(() => {
          this.addingParticipant = false;
          if (fromScanner)
            this.scannerMessage = {
              error: true,
              message: this.$t("message.error_generic")
            };
          else
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        });
    },
    processCheckinFromQr({ result }) {
      if (result) {
        if (this.checkingQrCode)
          return;
        this.checkingQrCode = true;
        this.scannerMessage = null;
        const split = result.split('|');
        if (split.length == 3) {
          if (split[1] != this.eventId || split[0] != this.$store.state.managedAcademy) {
            this.scannerMessage = {
              error: true,
              message: this.$t("message.invalid_event")
            }; this.checkingQrCode = false;
          } else {
            this.submitParticipantAttendance(split[2])
          }
        } else {
          this.checkingQrCode = false;
          this.scannerMessage = {
            error: true,
            message: this.$t("message.error")
          };
        }
      }
    },
    onLoad({ controls }) {
      this.qrScannerControls = controls;
      this.loadingScanner = false;
    },
    timeAtTimezone(date) {
      return new Date(date).toLocaleString(this.langToLocale(this.$i18n.locale), {
        timeZone: this.timeZone,
      });
    },
    openQRCode() {
      if (this.isSmallerDisplay()) {
        this.qrCodeUrl = this.event.publicLink;
        this.qrCodeDialog = true;
      } else {
        let buff = new Buffer(this.event.publicLink);
        const encodedUrl = buff.toString("base64");
        window.open(Environment.get("VUE_APP_ASSETS_HOME") + 'secure/qr/' + encodedUrl + '?sp=event&t=' + this.event.title);
      }
    },
    removeExtraTicket(i) {
      this.tickets.splice(i, 1);
    },
    addExtraTicket() {
      this.tickets.push({
        buttonLabel: 'Get Ticket',
        amountDecimal: 0,
        title: ' '
      });
    },
    checkIdentifier(val) {
      if (val) {
        this.checkingId = true;
        Api()
          .post(
            "utils/identifier/check",
            { term: val }
          )
          .then((response) => {
            if (response && response.data) {
              this.checkingId = false;
              if (response.data.valid) {
                this.validIdentifier = true;
                this.event.identifier = val;
                this.event.publicLink = 'https://core.bjjlink.com/pub/' + this.event.identifier;
              } else {
                this.validIdentifier = false;
                this.event.identifier = null;
              }
            }
          })
          .catch(() => {
            this.validIdentifier = false;
            this.checkingId = false;
          });
      }
    },
    copyAddress() {
      let textToCopy = this.address;
      this.copyToClipboard(textToCopy);
    },
    async sendEmailToParticipants() {
      this.sendingEmails = true;
      const recipients = [];
      var postData = {};
      if (this.participants) {
        for (const p of this.participants) {
          recipients.push(p.email);
        }
      }
      if (this.externalParticipants) {
        for (const p of this.externalParticipants) {
          recipients.push(p.email);
        }
      }
      if (recipients.length > 0 && this.emailContents != "" && this.emailSubject != "") {
        postData.recipients = recipients;
        postData.subject = this.emailSubject;
        postData.sendCopy = true;
        postData.contents = this.emailContents;
        //console.log(postData.contents);
        const regexp = /data:image\/([a-zA-Z]*);base64,([^"]*)/g;
        let totalImages = 0;

        const groups = this.findMatches(postData.contents, regexp);
        const replacements = [];

        if (groups.length >= 1) {
          let increaseBy = Math.round((100 - this.processingImagesProgress) / (totalImages + 1));
          this.processingImages = true;
          for (const group of groups) {
            try {
              const formData = new FormData();
              const d = new Date();
              const millis = d.getTime();

              const blob = this.blobFromBase64(group);
              formData.append("image", blob, 'bjjlink-user-admin-uploaded-image-' + millis + '.jpg');
              const res = await Api()
                .post("content/image/admin", formData);
              const data = res.data;
              if (data) {
                replacements.push(data.location);
                this.processingImagesProgress += increaseBy;
              }
            } catch (err) {
              console.log(err);
              this.processingImages = false;
              this.showAlert("error", "Error processing images");
              this.sendingEmails = false;
              this.sendEmailsDialog = false;
              return;
            }
          }
          this.processingImagesProgress = 100;
          postData.contents = this.replaceMatches(postData.contents, regexp, replacements);
        }

        this.postEmail(postData);
      }
    },
    postEmail(emailData) {
      this.processingImages = false;
      Api()
        .post(
          "admins/academy/" +
          this.$store.state.selectedAcademy +
          "/send/emails",
          emailData
        )
        .then((response) => {
          this.showAlert("success", "Oss!", ":)");
          if (response.data) {
            this.sendEmailsDialog = false;
            this.sendingEmails = false;
            this.emailSubject = "";
            this.emailContents = null;
          }
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        });
    },
    refundTransaction() {
      if (this.selectedParticipant) {
        var data = {
          amount: this.selectedParticipant.transaction.amount,
          transaction: this.selectedParticipant.transaction._id,
        };
        Api()
          .patch(
            "payments/academy/" +
            this.$store.state.managedAcademy +
            "/refund/" +
            this.selectedParticipant.transaction.paymentIntentId,
            data
          )
          .then((response) => {
            if (response.data) {
              this.getParticipants();
              this.closeTransactionDetails();
              this.showAlert("success", this.$t("message.success"));
            }
          })
          .catch((err) => {
            if (err)
              this.showAlert("error", this.$t("message.error_generic"));
          });
      }
    },
    goToTransaction(id) {
      this.navigateTo({
        name: "viewitemstransaction",
        params: { transactionId: id },
      });
    },
    goToCompletedDocument(id) {
      this.navigateTo({ name: 'viewcompleteddocument', params: { documentId: id } })
    },
    confirmRefund() {
      this.confirmRefundTransactionDiv = true;
    },
    removeParticipantFromEvent(p) {
      Api()
        .patch("admins/academy/" + this.$store.state.managedAcademy + "/events/" + this.eventId + "/participants/remove", { user: p.isUser ? p._id : null, email: !p.isUser ? p._id : null })
        .then((response) => {
          if (response) {
            this.showAlert("success", this.$t("message.success"));
            this.getParticipants();
            this.closeTransactionDetails();
          }
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        });
    },
    addParticipantToEvent(p) {
      Api()
        .patch("admins/academy/" + this.$store.state.managedAcademy + "/events/" + this.eventId + "/participants/add", { user: p.isUser ? p._id : null, email: !p.isUser ? p._id : null })
        .then((response) => {
          if (response) {
            this.showAlert("success", this.$t("message.success"));
            this.getParticipants();
            this.closeTransactionDetails();
          }
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        });
    },
    cancelPurchase(id) {
      Api()
        .patch(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/payments/cancel/checkout/" +
          id
        )
        .then((response) => {
          if (response) {
            this.showAlert(
              "success",
              this.$t("message.success"),
              this.$t("message.transaction_cancelled")
            );
            this.getParticipants();
          }
        });
    },
    completePurchase(id) {
      Api()
        .patch(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/payments/complete/checkout/" +
          id
        )
        .then((response) => {
          if (response) {
            this.showAlert(
              "success",
              this.$t("message.success"),
              this.$t("message.transaction_completed")
            );
            this.getEvent();
          }
        }).catch((e) => {
          if (e)
            this.getEvent();
        });
    },
    getPaymentMethod() {
      Api()
        .get(
          "payments/academy/" +
          this.$store.state.managedAcademy +
          "/customer/" +
          this.selectedMember
        )
        .then((response) => {
          if (response.data && response.data.paymentMethod) {
            this.paymentMethod = response.data.paymentMethod;
            /*
            if (this.product && this.product.type == "service")
              this.saveCreditCard = true;
            */
            let expDate = new Date();
            expDate.setFullYear(this.paymentMethod.expYear);
            expDate.setMonth(this.paymentMethod.expMonth - 1);
            this.paymentMethod.expDate = expDate;
            const today = new Date();
            if (expDate > today) this.paymentMethod.expired = false;
            else this.paymentMethod.expired = true;
          } else {
            this.paymentMethod = null;
          }
        });
    },
    displayTransactionDetails(user) {
      this.selectedParticipant = user;
      this.participantDetailsDialog = true;
    },
    closeTransactionDetails() {
      this.selectedParticipant = null;
      this.participantDetailsDialog = false;
      this.confirmRefundTransactionDiv = false;
      this.confirmRefundTransaction = false;
    },
    isTransactionFree(transaction) {
      if (!transaction) return false;
      const coupon = transaction.coupon;
      const hasCoupon = coupon && Object.keys(coupon).length > 0;
      if (hasCoupon) return coupon.discountedTotal === 0;
      return transaction.amount === 0;
    },
    getPaymentAmount(t) {
      let total = 0;
      if (t) {
        if (t.paidWithCash) {
          return t.amount;
        } else if (!t.refunds || t.refunds.length == 0) {
          return t.amount;
        }
      }
      return total;
    },
    getParticipants() {
      this.loadingParticipants = true;
      //get them from back end and process with transactions and more.... trasnactions will be used for refunds
      Api()
        .get("admins/academy/" + this.$store.state.managedAcademy + "/events/" + this.eventId + '/participants')
        .then((response) => {
          if (response.data) {
            let profits = 0;
            const data = response.data;
            if (data.participants) {
              this.participants = [];
              for (const d of data.participants) {
                const p = d.participant;
                this.participants.push({
                  _id: p._id, email: p.email, name: p.displayName, image: p.userImageSmall, isUser: true, isMember: true, transaction: d.transaction, checkedIn: d.checkedIn, documentCompleted: d.documentCompleted
                });
                profits += this.getPaymentAmount(d.transaction);
              }
            }
            if (data.externalParticipants) {
              this.externalParticipants = [];
              for (const d of data.externalParticipants) {
                const p = d.participant;
                this.externalParticipants.push({
                  _id: p._id ? p._id : p.email, email: p.email, name: p.displayName ? p.displayName : p.email, image: p.userImageSmall ? p.userImageSmall : null, isUser: p._id ? true : false, transaction: d.transaction, checkedIn: d.checkedIn, documentCompleted: d.documentCompleted
                });
                profits += this.getPaymentAmount(d.transaction);
              }
            }
            if (data.removedParticipants) {
              this.removedParticipants = [];
              for (const d of data.removedParticipants) {
                const p = d.participant;
                this.removedParticipants.push({
                  _id: p._id, name: p.displayName ? p.displayName : p.email, image: p.userImageSmall ? p.userImageSmall : null, isUser: p.displayName ? true : false, transaction: d.transaction, removed: true, checkedIn: d.checkedIn
                });
                profits += this.getPaymentAmount(d.transaction);
              }
            }
            this.totalSales = profits;
            this.loadingParticipants = false;
          } else {
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
          }
        });
    },
    clearAltImage() {
      this.event.altImage = null;
      this.altEventImage = null;
    },
    openSellTicketDialog() {
      this.sellTicketDialog = true;
    },
    sellTicket(cash, ticketOption) {
      const items = this.setupItems(ticketOption);
      this.sellingTicket = true;
      if (!items) {
        this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        this.sellingTicket = false;
        return;
      }
      let postUrl = "";
      let postData = {};
      if (cash) {
        postUrl = "payments/academy/" + this.$store.state.managedAcademy + "/onetime/cash/event";
        postData = {
          items: items,
          processedBy: "desk",
          buyer: this.selectedMember,
          quantity: 1,
          email: this.email,
          event: this.eventId
        };
      } else {
        postUrl = "payments/academy/" + this.$store.state.managedAcademy + "/onetime/credit/event";
        postData = {
          items: items,
          processedBy: "desk",
          buyer: this.selectedMember,
          quantity: 1,
          email: this.email,
          event: this.eventId
        };
      }
      Api()
        .post(postUrl, postData)
        .then((response) => {
          if (response.data) {
            if (cash) {
              this.showAlert("success", this.$t("message.success"));
              this.sellingTicket = false;
              this.sellTicketDialog = false;
              this.email = null;
              this.selectedMember = null;
              if (!this.freeTicket) {
                let num = new Number(this.amountDecimal).valueOf();
                if (ticketOption >= 0) {
                  const ticket = this.tickets[ticketOption];
                  if (!ticket)
                    return null;
                  num = ticket.amountDecimal;
                }
                this.totalSales += num;
                const newTotal = this.totalSales;
                this.totalSales = newTotal;
              }
              this.getParticipants();
            } else {
              this.navigateTo({
                name: "striperedirect_session",
                params: { sessionId: response.data.id },
              });
            }
          } else {
            this.sellingTicket = false;
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
          }
        })
        .catch((e) => {
          this.sellingTicket = false;
          if (e && e.response.status == 409)
            this.showAlert("error", "Oops!", this.$t("message.already_participant"));
          else
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        });
    },
    openPublicUrl() {
      window.open(this.event.publicLink);
    },
    copyPublicUrl() {
      let urlToCopy = this.event.publicLink;
      this.copyToClipboard(urlToCopy);
    },
    updateTimeZone(values) {
      if (values && values.longitude && values.latitude) {
        Api()
          .post("cities/timezone", values)
          .then((response) => {
            const data = response.data;
            if (data) {
              this.event.timeZone = data.timeZone;
              this.timeZone = data.timeZone;
              this.event.geolocated.timeZone = data.timeZone;
            }
          });
      }
    },
    getEvent() {
      Api()
        .get("admins/academy/" + this.$store.state.managedAcademy + "/events/" + this.eventId)
        .then((response) => {
          if (response.data && response.data.event && response.data.event._id) {
            let event = response.data.event;
            event.start = new Date(event.date);
            if (event.endDate) {
              event.end = new Date(event.endDate);
            } else {
              let start = new Date(event.date);
              start.setHours(start.getHours() + 1);
            }
            this.event = event;
            if (this.event.ticket) {
              this.amountDecimal = this.event.ticket.amountDecimal + "";
              this.addTicket = true;
            }
            this.canCheckin = response.data.canCheckin;
            // Fall back to the academy's own configured timezone when the event's city
            // doesn't have one cached yet (e.g. a brand-new event defaulted to the
            // academy's location) — the backend already sends it for exactly this.
            this.timeZone = (this.event.city && this.event.city.timeZone)
              ? this.event.city.timeZone
              : response.data.timeZone;
            this.browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            //console.log('Broswertimezone: ', browserTimezone)
            if (this.timeZone != this.browserTimezone) {
              this.differentTimezone = true;
            }
            if (this.event.tickets && this.event.tickets.length > 0)
              this.tickets = this.event.tickets;
            if (this.event.image)
              this.eventImage = this.event.image;
            if (this.event.document)
              this.enableDocument = true;
            if (this.event.altImage)
              this.altEventImage = this.event.altImage;
            if (this.event.identifier) {
              this.eventIdentifier = this.event.identifier;
              this.event.publicLink = 'https://core.bjjlink.com/pub/' + this.event.identifier;
            } else {
              this.event.publicLink = 'https://core.bjjlink.com/pub/' + this.eventId;
            }
            if (!this.event.ticket) {
              this.event.ticket = {
                amountDecimal: 0,
                title: '',
                hide: false,
                buttonLabel: this.$t('message.get_ticket')
              };
            }
            this.getParticipants();
            this.loading = false;
          } else {
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
          }
        })
        .catch((e) => {
          console.log(e)
          if (e)
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        });
    },
    setEventDate() {
      /*
      TODO: add this when we want to adjust time to TZ
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      //console.log('Broswertimezone: ', browserTimezone)
      if (this.event.timeZone != browserTimezone) {
        console.log('Different timezone: ', browserTimezone);
      } else {
        console.log('Same timezone: ', browserTimezone);
      }
      */
      if (this.event.start) {
        //console.log('We have a start: ', this.event.start);
        this.event.date = this.event.start;
      }
      if (this.event.end) {
        //console.log('We have an end: ', this.event.end);
        this.event.endDate = this.event.end;
      }
    },
    checkTicketInfo() {
      if (this.event.ticket && this.amountDecimal) {
        if (isNaN(this.amountDecimal)) {
          this.showAlert("error", this.$t("message.invalid_ticket_info"));
          return false;
        }
        const num = new Number(this.amountDecimal).valueOf();
        this.event.ticket.amountDecimal = num;
        return true;
      }
      return true;
    },
    missingEventFieldsMessage() {
      const missing = [];
      if (!this.event.title) missing.push(this.$t("message.title"));
      if (!this.event.date) missing.push(this.$t("message.date"));
      if (missing.length === 0) return null;
      return this.$t("message.missing_fields_list", [missing.join(", ")]);
    },
    addEvent() {
      this.setEventDate();
      const missingFields = this.missingEventFieldsMessage();
      if (missingFields) {
        this.showAlert("error", this.$t("message.fill_all_params"), missingFields);
        return false;
      }
      const proceed = this.checkTicketInfo();
      if (proceed) {
        this.event.image = this.eventImage;
        if (!this.addTicket)
          this.event.ticket = null;
        Api()
          .post("admins/academy/" + this.$store.state.managedAcademy + "/events", this.event)
          .then((response) => {
            if (response.data && response.data.event && response.data.event._id) {
              this.navigateTo({
                name: "manageacademyeventplus",
                params: { eventId: response.data.event._id },
              });
            } else {
              this.showAlert("error", "Oops!", this.$t("message.error_generic"));
            }
          })
          .catch((e) => {
            if (e)
              this.showAlert("error", "Oops!", this.$t("message.error_generic"));
          });
      } else {
        this.showAlert("error", "Oops!", this.$t("message.error_generic"));
      }
    },
    editEvent() {
      this.setEventDate();
      const missingFields = this.missingEventFieldsMessage();
      if (missingFields) {
        this.showAlert("error", this.$t("message.fill_all_params"), missingFields);
        return false;
      }
      const proceed = this.checkTicketInfo();
      if (proceed) {
        this.event.image = this.eventImage;
        if (this.tickets.length > 0)
          this.event.tickets = this.tickets;
        if (!this.addTicket)
          this.event.ticket = null;
        Api()
          .patch("admins/academy/" + this.$store.state.managedAcademy + "/events", this.event)
          .then((response) => {
            if (response) {
              this.showAlert("success", this.$t("message.success"));
            }
          })
          .catch((e) => {
            if (e)
              this.showAlert("error", "Oops!", this.$t("message.error_generic"));
          });
      } else {
        this.showAlert("error", "Oops!", this.$t("message.error_generic"));
      }
    },
    cancelEvent() {
      Api()
        .delete("admins/academy/" + this.$store.state.managedAcademy + "/events/" + this.event._id)
        .then((response) => {
          if (response) {
            this.event.cancelled = new Date();
            this.cancelEventDialog = false;
            this.showAlert("success", this.$t("message.success"));
            this.getEvent();
          }
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        });
    },
    setupItems(ticketOption) {
      let image = null;
      if (this.eventImage)
        image = this.userFile(this.eventImage);
      let num = new Number(this.amountDecimal).valueOf();
      let title = this.event.title;
      if (ticketOption >= 0) {
        const ticket = this.tickets[ticketOption];
        if (!ticket)
          return null;
        num = ticket.amountDecimal;
      }
      const items = [
        {
          name: title,
          description: title,
          images: [image],
          amountDecimal: this.freeTicket ? 0 : num,
          currency: this.currency ? this.currency : 'USD',
          quantity: 1,
        },
      ];
      return items;
    },
  },
  computed: {
    totalParticipants() {
      return this.externalParticipants.length + this.participants.length;
    },
    allParticipants() {
      return this.participants.concat(this.externalParticipants);
    },
  }
};
</script>

<style scoped>
.total-sales {
  font-weight: bold;
}

.input-message {
  margin-top: -20px;
}

.manage-academy-event-plus__section {
  border-color: rgba(0, 0, 0, 0.08) !important;
}

.event-image-upload-zone {
  border: 2px dashed #BDBDBD;
  background-color: #FAFAFA;
  border-radius: 8px;
  min-height: 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.event-image-upload-zone__preview {
  width: 100%;
  max-height: 160px;
  border-radius: 8px;
  overflow: hidden;
}

.event-image-upload-zone__preview img {
  width: 100%;
  height: 100%;
  max-height: 160px;
  object-fit: cover;
  display: block;
}

.event-image-upload-zone__placeholder {
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.event-timezone-field {
  max-width: 100%;
}

@media (min-width: 960px) {
  .event-timezone-field {
    max-width: 60%;
  }
}

.page-info-editor {
  min-height: 200px;
}

.page-info-editor ::v-deep .ql-container {
  min-height: 160px;
}
</style>
